import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import LabelInput from '../components/LabelInput';
import { useAuth } from '../context/Auth.context';
import {
  Button,
  Box,
  Heading,
  VStack,
  FormControl,
  Flex,
  Stack,
} from '@chakra-ui/react';

export default function Register() {
  const { error, loading, register } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');

  const methods = useForm();
  const { handleSubmit, watch, setError } = methods;

  const handleCancel = () => {
    navigate('/');
  };

  const normalizePhone = input => {
    const cleaned = input.replace(/\s+/g, '');
    if (/^04\d{8}$/.test(cleaned)) {
      return '+32' + cleaned.slice(1);
    }
    if (/^0032\d{9}$/.test(cleaned)) {
      return '+' + cleaned.slice(2);
    }
    return cleaned;
  };

  const handleRegister = useCallback(
    async ({ userName, email, password, phoneNumber }) => {
      const normalized = normalizePhone(phoneNumber);

      const belgischGsmRegex = /^\+32\d{9}$/;
      if (!belgischGsmRegex.test(normalized)) {
        setMessage('Ongeldig telefoonnummer, gebruik bv. 0476 123 456 of +32 476 123 456');
        return;
      }

      try {
        const result = await register({
          userName,
          email,
          password,
          phoneNumber: normalized,
        });

        if (!result.success) {
          if (result.code === 'VALIDATION_FAILED' && result.details?.body) {
            Object.entries(result.details.body).forEach(([field, errors]) => {
              setError(field, { type: 'manual', message: errors[0].message });
            });
            return;
          }
          setMessage(result.message);
          return;
        }

        const gebruiker = document.getElementById('registerForm').elements['email'].value;
        localStorage.setItem('email', gebruiker);
        navigate('/');
      } catch (err) {
        const resp = err.response?.data;
        if (resp?.code === 'VALIDATION_FAILED' && resp.details?.body) {
          Object.entries(resp.details.body).forEach(([field, errors]) => {
            setError(field, { type: 'manual', message: errors[0].message });
          });
        } else {
          setMessage(resp?.message || 'Er is iets misgegaan, probeer later opnieuw.');
        }
      }
    },
    [register, navigate, setError]
  );

  return (
    <FormProvider {...methods}>
      <Flex
        as="form"
        id="registerForm"
        onSubmit={handleSubmit(handleRegister)}
        direction="column"
        align="center"
        justify="flex-start"
        minH="100vh"
        px={4}
        pt={{ base: '20vh', md: '25vh', lg: '15vh' }}
      >
        <Box w={['100%', '90%', '70%', '800px']} maxW="800px">
          <Heading as="h1" mb={6} textAlign="center">
            Registreer
          </Heading>

          {message && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              color="red.700"
              p={3}
              borderRadius="md"
              textAlign="center"
              mb={4}
              ml={0}
            >
              {message}
            </Box>
          )}

          <Stack spacing={4}>
            <FormControl>
              <LabelInput
                label="Naam"
                type="text"
                name="userName"
                placeholder="Uw naam"
                data-cy="registerName"
                validationRules={{
                  required: 'Naam is verplicht',
                  minLength: {
                    value: 2,
                    message: 'Naam moet minstens 2 tekens lang zijn',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <LabelInput
                label="Email"
                type="text"
                name="email"
                placeholder="your@email.com"
                data-cy="registerEmail"
                validationRules={{
                  required: 'Email is verplicht',
                  pattern: {
                    value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                    message: 'Voer een geldig e-mailadres in (inclusief "@" en ".")',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <LabelInput
                label="Telefoonnummer"
                type="text"
                name="phoneNumber"
                placeholder="+32 476 123 456"
                data-cy="registerPhone"
                validationRules={{
                  required: 'Telefoonnummer is verplicht',
                  validate: value => {
                    const norm = normalizePhone(value);
                    return (
                      /^\+32\d{9}$/.test(norm) ||
                      'Gebruik een Belgisch gsm-nummer: bv. 0476 123 456 of +32 476 123 456'
                    );
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <LabelInput
                label="Wachtwoord"
                type="password"
                name="password"
                validationRules={{
                  required: 'Wachtwoord is verplicht',
                  minLength: {
                    value: 8,
                    message: 'Wachtwoord moet minstens 8 tekens lang zijn',
                  },
                }}
                data-cy="registerPassword"
              />
            </FormControl>

            <FormControl>
              <LabelInput
                label="Bevestig wachtwoord"
                type="password"
                name="confirmPassword"
                validationRules={{
                  validate: (value) =>
                    value === watch('password') || 'Wachtwoorden komen niet overeen',
                }}
                data-cy="registerConfirmPassword"
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              data-cy="register"
              w="full"
              ml={0}
            >
              Registreer
            </Button>

            <Button
              onClick={handleCancel}
              colorScheme="gray"
              _hover={{ backgroundColor: 'darkgray' }}
              isLoading={loading}
              w="full"
              ml={0}
            >
              Annuleer
            </Button>
          </Stack>
        </Box>
      </Flex>
    </FormProvider>
  );
}
